import { c as _c } from "react/compiler-runtime";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/blacksmith-ui/avatar";
import { useActiveGithubOrgForUserNonSuspense, useConnectedGithubOrgsNonSuspense, useIsImpersonatingNonSuspense, type SsoBlockedOrg } from "@/hooks/useConnectedGithubOrgs";
import { clearSsoReauthPending, githubReconnectLogin, markSsoReauthPending } from "@/lib/github-reauth";
import { orgTargetPath } from "@/lib/orgTargetPath";
import { useSsoReauthPrompt } from "@/hooks/useSsoReauthPrompt";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { safeLocalStorage } from "@/lib/storage";
import { cn } from "@repo/blacksmith-ui/cn";
import { ChevronRight, ShieldAlert, Sparkles, Check, Plus, RefreshCw, X } from "lucide-react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { BlackGitHubIcon, GitHubIcon, WhiteGitHubIcon } from "./GitHubIcon";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@repo/blacksmith-ui/dropdown-menu";
import { Popover, PopoverAnchor, PopoverContent } from "@repo/blacksmith-ui/popover";
const EMPTY_ORGS: string[] = [];
export interface OrgSelectorOrg {
  login: string;
  avatarUrl: string;
  isNew?: boolean;
  isSuspended?: boolean;
  /** Route to navigate to when this org is selected. When set, the row
   * renders as a real link so modified clicks open in a new tab. */
  href?: string;
}
export interface GitHubOrgSelectorViewProps {
  orgs: OrgSelectorOrg[];
  activeOrgLogin?: string | null;
  /**
   * Orgs GitHub hid from the user's account because their OAuth token lacks
   * an active SAML SSO authorization. Surfaced as an amber notification dot
   * on the trigger and highlighted rows in the dropdown that deep-link to
   * GitHub's SSO authorization flow.
   */
  ssoBlockedOrgs?: SsoBlockedOrg[];
  isImpersonating?: boolean;
  /** Selection callback for org rows without an `href`; rows with an `href`
   * navigate via the link itself. */
  onSelectOrg?: (login: string) => void;
  onAddOrg: () => void;
  /** GitHub App installation URL; when set the "Add an organization" menu
   * item renders as a real link so modified clicks open in a new tab. */
  addOrgHref?: string;
  onAuthorizeOrg?: (org: SsoBlockedOrg) => void;
  /**
   * Blocked orgs the user has already clicked Authorize for. Their row swaps
   * Authorize for Reconnect (the silent OAuth re-login that actually restores
   * access); once the re-login lands the flag is gone, so an org that is still
   * blocked falls back to Authorize.
   */
  ssoReauthPendingOrgs?: string[];
  onReconnect?: () => void;
  /** Opens the Reconnect callout that pops out of the trigger. Only true once
   * the user has come back from GitHub, so a blocked popup is not greeted as
   * a finished authorization. */
  ssoReconnectCalloutOpen?: boolean;
  /** Dismisses the Reconnect callout without reconnecting. */
  onDismissReconnect?: () => void;
  /**
   * Whether the enclosing app-switcher rail is expanded. When collapsed the
   * trigger's border fades out so the org avatar reads as a bare icon in the
   * slim rail; it fades back in as the rail wipes open. Defaults to `true` so
   * standalone/story usages keep the bordered pill.
   */
  expanded?: boolean;
  /**
   * True while org data is still being fetched. `orgs` is empty during this
   * window too, so without this flag the empty-state ("Add an organization",
   * which renders a GitHub glyph) flashes on every refresh before the real
   * org avatar arrives. When loading, render the bordered pill with the
   * active org login when one is known (org routes), or a skeleton pill when
   * there is nothing to show yet (org-less routes).
   */
  isLoading?: boolean;
}
export function GitHubOrgSelectorView(t0) {
  const $ = _c(98);
  const {
    orgs,
    activeOrgLogin,
    ssoBlockedOrgs: t1,
    isImpersonating: t2,
    onSelectOrg,
    onAddOrg,
    addOrgHref,
    onAuthorizeOrg,
    ssoReauthPendingOrgs: t3,
    onReconnect,
    ssoReconnectCalloutOpen: t4,
    onDismissReconnect,
    expanded: t5,
    isLoading: t6
  } = t0;
  let t7;
  if ($[0] !== t1) {
    t7 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const ssoBlockedOrgs = t7;
  const isImpersonating = t2 === undefined ? false : t2;
  const ssoReauthPendingOrgs = t3 === undefined ? EMPTY_ORGS : t3;
  const ssoReconnectCalloutOpen = t4 === undefined ? false : t4;
  const expanded = t5 === undefined ? true : t5;
  const isLoading = t6 === undefined ? false : t6;
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const {
    resolvedTheme: theme
  } = useTheme();
  let t8;
  let t9;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const handleOpenOrgSwitcher = () => {
        setIsOpen(true);
      };
      window.addEventListener("openOrgSwitcher", handleOpenOrgSwitcher);
      return () => {
        window.removeEventListener("openOrgSwitcher", handleOpenOrgSwitcher);
      };
    };
    t9 = [];
    $[2] = t8;
    $[3] = t9;
  } else {
    t8 = $[2];
    t9 = $[3];
  }
  useEffect(t8, t9);
  let t10;
  if ($[4] !== activeOrgLogin || $[5] !== orgs) {
    t10 = orgs.find(org => org.login === activeOrgLogin) ?? orgs.at(0);
    $[4] = activeOrgLogin;
    $[5] = orgs;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const activeOrg = t10;
  let t11;
  if ($[7] !== orgs) {
    t11 = orgs.some(_temp);
    $[7] = orgs;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const hasNewInstallations = t11;
  const hasSsoBlockedOrgs = ssoBlockedOrgs.length > 0;
  let t12;
  if ($[9] !== ssoBlockedOrgs || $[10] !== ssoReauthPendingOrgs) {
    let t13;
    if ($[12] !== ssoReauthPendingOrgs) {
      t13 = org_1 => ssoReauthPendingOrgs.includes(org_1.name);
      $[12] = ssoReauthPendingOrgs;
      $[13] = t13;
    } else {
      t13 = $[13];
    }
    t12 = ssoBlockedOrgs.filter(t13);
    $[9] = ssoBlockedOrgs;
    $[10] = ssoReauthPendingOrgs;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const reconnectPendingOrgs = t12;
  if (!orgs.length && isLoading && !activeOrgLogin && !isImpersonating) {
    let t13;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <Skeleton className="h-8 w-full rounded-lg" />;
      $[14] = t13;
    } else {
      t13 = $[14];
    }
    return t13;
  }
  if (!orgs.length && !isLoading) {
    let t13;
    if ($[15] !== theme) {
      t13 = <div className="shrink-0">{theme === "dark" ? <WhiteGitHubIcon /> : <BlackGitHubIcon />}</div>;
      $[15] = theme;
      $[16] = t13;
    } else {
      t13 = $[16];
    }
    let t14;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <span className="justify-start w-40 truncate text-neutral-900 dark:text-neutral-100">Add an organization</span>;
      $[17] = t14;
    } else {
      t14 = $[17];
    }
    let t15;
    if ($[18] !== t13) {
      t15 = <DropdownMenuTrigger className="focus-visible:outline-hidden"><span className="flex items-center">{t13}{t14}</span></DropdownMenuTrigger>;
      $[18] = t13;
      $[19] = t15;
    } else {
      t15 = $[19];
    }
    let t16;
    if ($[20] !== isOpen || $[21] !== t15) {
      t16 = <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>{t15}</DropdownMenu>;
      $[20] = isOpen;
      $[21] = t15;
      $[22] = t16;
    } else {
      t16 = $[22];
    }
    let t17;
    if ($[23] !== onAddOrg || $[24] !== t16) {
      t17 = <div className="rounded-xl border-[1px] border-[var(--color-border)] px-3 py-2 text-neutral-500 text-sm font-medium w-50" onClick={onAddOrg}>{t16}</div>;
      $[23] = onAddOrg;
      $[24] = t16;
      $[25] = t17;
    } else {
      t17 = $[25];
    }
    return t17;
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = open => {
      setIsOpen(open);
      if (!open) {
        setTimeout(() => {
          triggerRef.current?.blur();
        }, 0);
      }
    };
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const handleOpenChange = t13;
  const t14 = expanded ? "border-[#0c0c0d21] dark:border-white/16" : "border-transparent dark:border-transparent";
  let t15;
  if ($[27] !== t14) {
    t15 = cn("relative flex items-center w-full h-8 gap-2 px-1 overflow-hidden rounded-lg border bg-transparent focus-visible:outline-hidden cursor-pointer", "[transition:border-color_240ms_cubic-bezier(0.22,1,0.36,1)]", t14);
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== activeOrg?.avatarUrl || $[30] !== activeOrg?.login || $[31] !== activeOrgLogin || $[32] !== isImpersonating) {
    t16 = <Avatar className="size-[22px] shrink-0 border border-[#0c0c0d21] dark:border-white/24">{isImpersonating ? <ShieldAlert className="aspect-square h-full w-full text-orange-500" /> : <><AvatarImage src={activeOrg?.avatarUrl ?? ""} alt="active org logo" loading="eager" /><AvatarFallback className="uppercase">{(activeOrg?.login ?? activeOrgLogin)?.charAt(0)}</AvatarFallback></>}</Avatar>;
    $[29] = activeOrg?.avatarUrl;
    $[30] = activeOrg?.login;
    $[31] = activeOrgLogin;
    $[32] = isImpersonating;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = isImpersonating && "text-orange-500 font-bold";
  let t18;
  if ($[34] !== t17) {
    t18 = cn("flex-1 text-left truncate type-body-s-regular text-neutral-950 dark:text-white", t17);
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const t19 = (isImpersonating ? activeOrgLogin : activeOrg?.login ?? activeOrgLogin) ?? "";
  let t20;
  if ($[36] !== t18 || $[37] !== t19) {
    t20 = <span className={t18}>{t19}</span>;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <ChevronRight className="size-[18px] shrink-0 rotate-90 text-neutral-500 dark:text-neutral-400" />;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t15 || $[41] !== t16 || $[42] !== t20) {
    t22 = <DropdownMenuTrigger ref={triggerRef} className={t15}>{t16}{t20}{t21}</DropdownMenuTrigger>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t20;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => {
      triggerRef.current?.blur();
    };
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== addOrgHref || $[46] !== onAddOrg) {
    t24 = addOrgHref ? <DropdownMenuItem key="add-new-org" asChild={true} className="flex gap-3 items-center h-[40px] cursor-pointer rounded-sm px-3"><a href={addOrgHref} onClick={() => setIsOpen(false)}><div className="flex items-center justify-center w-6 h-6 rounded-full bg-neutral-100 dark:bg-neutral-700 shrink-0"><Plus className="w-4 h-4 text-neutral-950 dark:text-white" /></div><span className="flex items-center gap-2 truncate text-neutral-950 dark:text-white text-sm">Add an organization</span></a></DropdownMenuItem> : <DropdownMenuItem key="add-new-org" className="flex gap-3 items-center h-[40px] cursor-pointer rounded-sm px-3" onClick={() => {
      setIsOpen(false);
      onAddOrg();
    }}><div className="flex items-center justify-center w-6 h-6 rounded-full bg-neutral-100 dark:bg-neutral-700 shrink-0"><Plus className="w-4 h-4 text-neutral-950 dark:text-white" /></div><span className="flex items-center gap-2 truncate text-neutral-950 dark:text-white text-sm">Add an organization</span></DropdownMenuItem>;
    $[45] = addOrgHref;
    $[46] = onAddOrg;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <DropdownMenuSeparator />;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== activeOrgLogin || $[50] !== onSelectOrg || $[51] !== orgs) {
    let t27;
    if ($[53] !== activeOrgLogin || $[54] !== onSelectOrg) {
      t27 = org_2 => {
        const rowContents = <><div className="flex items-center justify-center w-6 h-6 rounded-full bg-neutral-100 dark:bg-neutral-700 shrink-0 overflow-hidden"><Avatar className="w-6 h-6"><AvatarImage src={org_2.avatarUrl} alt={`${org_2.login} logo`} loading="eager" /><AvatarFallback className="uppercase text-xs">{org_2.login?.charAt(0)}</AvatarFallback></Avatar></div><span className="truncate flex-1 text-neutral-950 dark:text-white text-sm">{org_2.login}</span>{org_2.login === activeOrgLogin && <Check className="w-4 h-4 text-neutral-950 dark:text-white shrink-0" />}{org_2.isNew && <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><Sparkles className="w-3 h-3" /><span>New</span></span>}{org_2.isSuspended && <span className="text-xs text-red-500">Suspended</span>}</>;
        return org_2.href ? <DropdownMenuItem key={org_2.login} asChild={true} className="flex gap-3 items-center h-[40px] cursor-pointer rounded-sm px-3 relative"><Link href={org_2.href} onClick={() => setIsOpen(false)}>{rowContents}</Link></DropdownMenuItem> : <DropdownMenuItem key={org_2.login} className="flex gap-3 items-center h-[40px] cursor-pointer rounded-sm px-3 relative" onClick={() => {
          setIsOpen(false);
          onSelectOrg?.(org_2.login);
        }}>{rowContents}</DropdownMenuItem>;
      };
      $[53] = activeOrgLogin;
      $[54] = onSelectOrg;
      $[55] = t27;
    } else {
      t27 = $[55];
    }
    t26 = orgs.map(t27);
    $[49] = activeOrgLogin;
    $[50] = onSelectOrg;
    $[51] = orgs;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[56] !== hasSsoBlockedOrgs || $[57] !== onAuthorizeOrg || $[58] !== onReconnect || $[59] !== ssoBlockedOrgs || $[60] !== ssoReauthPendingOrgs) {
    t27 = hasSsoBlockedOrgs && <><DropdownMenuSeparator /><div className="flex items-center gap-1.5 px-3 pt-2 pb-1 type-body-xxs-regular text-amber-600 dark:text-amber-400"><ShieldAlert className="w-3 h-3 shrink-0" />Hidden until you re-authorize SSO</div>{ssoBlockedOrgs.map(org_3 => {
        const reconnectPending = ssoReauthPendingOrgs.includes(org_3.name);
        return <DropdownMenuItem key={org_3.name} className="flex gap-3 items-center h-[40px] cursor-pointer rounded-sm px-3 bg-amber-500/10 focus:bg-amber-500/20 dark:bg-amber-400/10 dark:focus:bg-amber-400/20" onClick={() => {
          if (reconnectPending) {
            onReconnect?.();
            return;
          }
          window.open(org_3.sso_url, "_blank", "noopener,noreferrer");
          onAuthorizeOrg?.(org_3);
        }}><div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/15 ring-1 ring-amber-500/50 shrink-0"><span className="uppercase text-xs text-amber-600 dark:text-amber-400">{org_3.name.charAt(0)}</span></div><span className="truncate flex-1 text-neutral-950 dark:text-white text-sm">{org_3.name}</span><span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 shrink-0">{reconnectPending ? <>Reconnect<RefreshCw className="w-3 h-3" /></> : <>Authorize<GitHubIcon className="w-3 h-3" /></>}</span></DropdownMenuItem>;
      })}</>;
    $[56] = hasSsoBlockedOrgs;
    $[57] = onAuthorizeOrg;
    $[58] = onReconnect;
    $[59] = ssoBlockedOrgs;
    $[60] = ssoReauthPendingOrgs;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <DropdownMenuSeparator />;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="px-3 py-2 type-body-xxs-regular text-neutral-950 dark:text-white text-center">Missing an org?{" "}<a href="https://docs.blacksmith.sh/introduction/quickstart#dont-see-your-organization-in-the-blacksmith-dashboard" target="_blank" rel="noopener noreferrer" className="text-neutral-900 dark:text-neutral-100 underline underline-offset-2 hover:text-neutral-700 dark:hover:text-neutral-300">Here's why!</a></div>;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t24 || $[65] !== t26 || $[66] !== t27) {
    t30 = <DropdownMenuContent align="start" side="bottom" sideOffset={0} alignOffset={0} className="type-body-s-regular w-[239px] rounded-lg border-[#0c0c0d14] bg-white shadow-[0px_10px_20px_0px_rgba(0,0,0,0.2)] dark:border-white/12 dark:bg-[#2A2C2C] dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.2)] backdrop-blur-none" onInteractOutside={t23}>{t24}{t25}{t26}{t27}{t28}{t29}</DropdownMenuContent>;
    $[64] = t24;
    $[65] = t26;
    $[66] = t27;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== isOpen || $[69] !== t22 || $[70] !== t30) {
    t31 = <DropdownMenu open={isOpen} onOpenChange={handleOpenChange} modal={false}>{t22}{t30}</DropdownMenu>;
    $[68] = isOpen;
    $[69] = t22;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== hasNewInstallations || $[73] !== hasSsoBlockedOrgs) {
    t32 = hasSsoBlockedOrgs ? <div className="pointer-events-none absolute -top-1 -right-1 flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" /></div> : hasNewInstallations ? <div className="pointer-events-none absolute -top-1 -right-1 flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" /></div> : null;
    $[72] = hasNewInstallations;
    $[73] = hasSsoBlockedOrgs;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  const t33 = ssoReconnectCalloutOpen && reconnectPendingOrgs.length > 0;
  let t34;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <PopoverAnchor aria-hidden={true} className="pointer-events-none absolute inset-0" />;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = cn("w-auto max-w-[min(392px,calc(100vw-2rem))] overflow-visible p-0 ring-0 text-neutral-950 dark:text-neutral-950", "rounded-xl border border-black/15 bg-amber-400 shadow-sm dark:border-white/15 dark:bg-amber-400", "animate-in fade-in-0 zoom-in-95 slide-in-from-left-2 duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-left-2 motion-reduce:animate-none");
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <RefreshCw className="size-4 shrink-0" aria-hidden={true} />;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <span className="type-body-xs-medium">Reconnect GitHub</span>;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  const t38 = reconnectPendingOrgs.length === 1 ? `Authorized ${reconnectPendingOrgs[0].name}? Reconnect to restore access.` : `Reconnect to restore access to the ${reconnectPendingOrgs.length} orgs you authorized.`;
  let t39;
  if ($[79] !== t38) {
    t39 = <span className="flex min-w-0 flex-col">{t37}<span className="type-body-xxs-regular">{t38}</span></span>;
    $[79] = t38;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  let t40;
  if ($[81] !== onReconnect || $[82] !== t39) {
    t40 = <button type="button" onClick={onReconnect} className="flex cursor-pointer items-center gap-2 rounded-xl py-2 pl-3 pr-8 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-black/30">{t36}{t39}</button>;
    $[81] = onReconnect;
    $[82] = t39;
    $[83] = t40;
  } else {
    t40 = $[83];
  }
  let t41;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <X size={12} strokeWidth={2.5} />;
    $[84] = t41;
  } else {
    t41 = $[84];
  }
  let t42;
  if ($[85] !== onDismissReconnect) {
    t42 = <button type="button" onClick={onDismissReconnect} aria-label="Dismiss reconnect reminder" className="absolute right-2 top-2 rounded-full p-0.5 transition-colors hover:bg-black/10">{t41}</button>;
    $[85] = onDismissReconnect;
    $[86] = t42;
  } else {
    t42 = $[86];
  }
  let t43;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <svg className="pointer-events-none absolute left-[-6px] top-1/2 -translate-y-1/2" width={7} height={14} viewBox="0 0 7 14" aria-hidden={true}><path d="M7 0 L0 7 L7 14 Z" className="fill-amber-400" /><path d="M6.43 0.57 L0 7 L6.43 13.43" fill="none" strokeWidth={1} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" className="stroke-black/15 dark:stroke-white/15" /></svg>;
    $[87] = t43;
  } else {
    t43 = $[87];
  }
  let t44;
  if ($[88] !== t40 || $[89] !== t42) {
    t44 = <PopoverContent side="right" align="center" sideOffset={8} onOpenAutoFocus={_temp2} onCloseAutoFocus={_temp3} className={t35}><div className="relative flex min-h-8 items-center">{t40}{t42}{t43}</div></PopoverContent>;
    $[88] = t40;
    $[89] = t42;
    $[90] = t44;
  } else {
    t44 = $[90];
  }
  let t45;
  if ($[91] !== t33 || $[92] !== t44) {
    t45 = <Popover open={t33}>{t34}{t44}</Popover>;
    $[91] = t33;
    $[92] = t44;
    $[93] = t45;
  } else {
    t45 = $[93];
  }
  let t46;
  if ($[94] !== t31 || $[95] !== t32 || $[96] !== t45) {
    t46 = <div className="relative w-full text-sm font-medium">{t31}{t32}{t45}</div>;
    $[94] = t31;
    $[95] = t32;
    $[96] = t45;
    $[97] = t46;
  } else {
    t46 = $[97];
  }
  return t46;
}
function _temp3(e_0) {
  return e_0.preventDefault();
}
function _temp2(e) {
  return e.preventDefault();
}
function _temp(org_0) {
  return org_0.isNew;
}
export function GitHubOrgSelector(t0) {
  const $ = _c(27);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    expanded: t2
  } = t1;
  const expanded = t2 === undefined ? true : t2;
  const router = useRouter();
  const {
    connectedGithubOrgs,
    isLoading
  } = useConnectedGithubOrgsNonSuspense();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const isImpersonating = useIsImpersonatingNonSuspense();
  let t3;
  if ($[2] !== connectedGithubOrgs?.sso_blocked_orgs) {
    t3 = connectedGithubOrgs?.sso_blocked_orgs ?? [];
    $[2] = connectedGithubOrgs?.sso_blocked_orgs;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const ssoBlockedOrgs = t3;
  const {
    pendingOrgs: ssoReauthPendingOrgs,
    returnedFromGithub
  } = useSsoReauthPrompt(connectedGithubOrgs ? ssoBlockedOrgs : undefined);
  const getVisitedOrgs = _temp4;
  let t4;
  let t5;
  if ($[4] !== activeGithubOrg) {
    t4 = () => {
      if (activeGithubOrg) {
        const visited = getVisitedOrgs();
        visited.add(activeGithubOrg);
        safeLocalStorage.set("blacksmith_visited_orgs", JSON.stringify(Array.from(visited)));
      }
    };
    t5 = [activeGithubOrg];
    $[4] = activeGithubOrg;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = org => {
      const createdDate = new Date(org.created_at);
      const now = new Date();
      const fifteenMinutesAgo = new Date(now.getTime() - 900000);
      const isRecent = createdDate > fifteenMinutesAgo;
      const isVisited = getVisitedOrgs().has(org.account.login);
      return isRecent && !isVisited;
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const isNewInstallation = t6;
  const routePattern = router.pathname;
  let t7;
  if ($[8] !== connectedGithubOrgs?.installations) {
    t7 = connectedGithubOrgs?.installations ?? [];
    $[8] = connectedGithubOrgs?.installations;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const installations = t7;
  let t8;
  if ($[10] !== activeGithubOrg || $[11] !== installations || $[12] !== routePattern) {
    let t9;
    if ($[14] !== activeGithubOrg || $[15] !== installations.length || $[16] !== routePattern) {
      t9 = org_0 => ({
        login: org_0.account.login,
        avatarUrl: org_0.account.avatar_url,
        href: orgTargetPath(routePattern, org_0.account.login),
        isNew: installations.length > 1 && org_0.account.login !== activeGithubOrg && org_0.account.type !== "User" && isNewInstallation(org_0),
        isSuspended: Boolean(org_0.suspended_by)
      });
      $[14] = activeGithubOrg;
      $[15] = installations.length;
      $[16] = routePattern;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    t8 = installations.map(t9);
    $[10] = activeGithubOrg;
    $[11] = installations;
    $[12] = routePattern;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const orgs = t8;
  let t9;
  if ($[18] !== activeGithubOrg || $[19] !== expanded || $[20] !== isImpersonating || $[21] !== isLoading || $[22] !== orgs || $[23] !== returnedFromGithub || $[24] !== ssoBlockedOrgs || $[25] !== ssoReauthPendingOrgs) {
    t9 = <GitHubOrgSelectorView orgs={orgs} activeOrgLogin={activeGithubOrg} ssoBlockedOrgs={ssoBlockedOrgs} isImpersonating={isImpersonating} isLoading={isLoading} expanded={expanded} addOrgHref={process.env.NEXT_PUBLIC_GITHUB_APP_INSTALLATION_URL} onAddOrg={_temp5} onAuthorizeOrg={_temp6} ssoReauthPendingOrgs={ssoReauthPendingOrgs} onReconnect={githubReconnectLogin} ssoReconnectCalloutOpen={returnedFromGithub} onDismissReconnect={clearSsoReauthPending} />;
    $[18] = activeGithubOrg;
    $[19] = expanded;
    $[20] = isImpersonating;
    $[21] = isLoading;
    $[22] = orgs;
    $[23] = returnedFromGithub;
    $[24] = ssoBlockedOrgs;
    $[25] = ssoReauthPendingOrgs;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}
function _temp6(org_1) {
  return markSsoReauthPending(org_1.name);
}
function _temp5() {
  window.location.href = `${process.env.NEXT_PUBLIC_GITHUB_APP_INSTALLATION_URL}`;
}
function _temp4() {
  const stored = safeLocalStorage.get("blacksmith_visited_orgs");
  if (!stored) {
    return new Set();
  }
  try {
    return new Set(JSON.parse(stored));
  } catch {
    return new Set();
  }
}